/**
 * Math Question UI
 * Displays math problems with answer buttons for honeypot phase
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { randomInt } from '../systems/Utils.js';

export class MathQuestion {
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.x = x;
        this.y = y;

        this.difficulty = options.difficulty || 1;
        this.onCorrect = options.onCorrect || (() => {});
        this.onWrong = options.onWrong || (() => {});

        // Container for question UI
        this.container = scene.add.container(x, y);

        // Current question state
        this.currentQuestion = null;
        this.correctAnswer = 0;
        this.answerButtons = [];

        // Create UI elements
        this.createUI();

        // Generate first question
        this.generateQuestion();
    }

    createUI() {
        // Question background
        this.questionBg = this.scene.add.graphics();
        this.questionBg.fillStyle(0xFFFFFF, 0.95);
        this.questionBg.fillRoundedRect(-200, -80, 400, 160, 16);
        this.questionBg.lineStyle(3, CONSTANTS.COLORS.CHEERFUL_ACCENT);
        this.questionBg.strokeRoundedRect(-200, -80, 400, 160, 16);
        this.container.add(this.questionBg);

        // Question text
        this.questionText = this.scene.add.text(0, -40, '', {
            fontSize: '36px',
            fontFamily: 'Georgia, serif',
            color: '#333333',
            fontStyle: 'bold'
        }).setOrigin(0.5);
        this.container.add(this.questionText);

        // Answer buttons will be created per question
    }

    generateQuestion() {
        // Clear previous answer buttons
        this.answerButtons.forEach(btn => {
            btn.button.destroy();
            btn.text.destroy();
        });
        this.answerButtons = [];

        // Generate question based on difficulty
        const question = this.createMathProblem();
        this.currentQuestion = question;
        this.correctAnswer = question.answer;

        // Display question
        this.questionText.setText(question.text + ' = ?');

        // Generate answer choices (1 correct, 3 wrong)
        const answers = this.generateAnswerChoices(question.answer);

        // Create answer buttons
        this.createAnswerButtons(answers);
    }

    createMathProblem() {
        const maxNum = 5 + Math.floor(this.difficulty * 3);

        // Simple operations based on difficulty
        const ops = this.difficulty < 3 ? ['+', '-'] : ['+', '-', '*'];
        const op = ops[randomInt(0, ops.length - 1)];

        let a, b, answer, text;

        switch (op) {
            case '+':
                a = randomInt(1, maxNum);
                b = randomInt(1, maxNum);
                answer = a + b;
                text = `${a} + ${b}`;
                break;

            case '-':
                a = randomInt(1, maxNum);
                b = randomInt(1, a); // Ensure positive result
                answer = a - b;
                text = `${a} - ${b}`;
                break;

            case '*':
                a = randomInt(1, Math.min(maxNum, 10));
                b = randomInt(1, Math.min(maxNum, 10));
                answer = a * b;
                text = `${a} × ${b}`;
                break;
        }

        return { text, answer, operation: op };
    }

    generateAnswerChoices(correctAnswer) {
        const choices = [correctAnswer];

        // Generate 3 wrong answers
        while (choices.length < 4) {
            // Wrong answers are close to correct to be challenging
            const offset = randomInt(1, 5) * (Math.random() < 0.5 ? 1 : -1);
            const wrongAnswer = correctAnswer + offset;

            // Avoid duplicates and negative numbers
            if (!choices.includes(wrongAnswer) && wrongAnswer >= 0) {
                choices.push(wrongAnswer);
            }
        }

        // Shuffle answers
        return this.shuffleArray(choices);
    }

    shuffleArray(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    createAnswerButtons(answers) {
        const buttonWidth = 80;
        const buttonHeight = 45;
        const spacing = 20;
        const startX = -((answers.length - 1) * (buttonWidth + spacing)) / 2;
        const buttonY = 30;

        answers.forEach((answer, index) => {
            const x = startX + index * (buttonWidth + spacing);

            // Button background
            const button = this.scene.add.graphics();
            button.fillStyle(0x5C6BC0);
            button.fillRoundedRect(
                x - buttonWidth / 2,
                buttonY - buttonHeight / 2,
                buttonWidth,
                buttonHeight,
                8
            );

            // Make interactive zone
            const hitArea = this.scene.add.rectangle(
                x, buttonY, buttonWidth, buttonHeight, 0x000000, 0
            ).setInteractive({ useHandCursor: true });

            // Button text
            const text = this.scene.add.text(x, buttonY, answer.toString(), {
                fontSize: '24px',
                fontFamily: 'Arial',
                color: '#FFFFFF',
                fontStyle: 'bold'
            }).setOrigin(0.5);

            // Hover effects
            hitArea.on('pointerover', () => {
                button.clear();
                button.fillStyle(0x7986CB);
                button.fillRoundedRect(
                    x - buttonWidth / 2,
                    buttonY - buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                    8
                );
            });

            hitArea.on('pointerout', () => {
                button.clear();
                button.fillStyle(0x5C6BC0);
                button.fillRoundedRect(
                    x - buttonWidth / 2,
                    buttonY - buttonHeight / 2,
                    buttonWidth,
                    buttonHeight,
                    8
                );
            });

            // Click handler
            hitArea.on('pointerdown', () => {
                this.handleAnswer(answer);
            });

            this.container.add(button);
            this.container.add(hitArea);
            this.container.add(text);

            this.answerButtons.push({ button, text, hitArea, answer });
        });
    }

    handleAnswer(selectedAnswer) {
        if (selectedAnswer === this.correctAnswer) {
            // Correct!
            this.flashCorrect();
            this.onCorrect();

            // Generate new question after brief delay
            this.scene.time.delayedCall(500, () => {
                this.generateQuestion();
            });
        } else {
            // Wrong!
            this.flashWrong();
            this.onWrong();
        }
    }

    flashCorrect() {
        // Green flash on background
        this.questionBg.clear();
        this.questionBg.fillStyle(0x4CAF50, 0.9);
        this.questionBg.fillRoundedRect(-200, -80, 400, 160, 16);

        this.scene.time.delayedCall(300, () => {
            this.questionBg.clear();
            this.questionBg.fillStyle(0xFFFFFF, 0.95);
            this.questionBg.fillRoundedRect(-200, -80, 400, 160, 16);
            this.questionBg.lineStyle(3, CONSTANTS.COLORS.CHEERFUL_ACCENT);
            this.questionBg.strokeRoundedRect(-200, -80, 400, 160, 16);
        });
    }

    flashWrong() {
        // Red flash on background
        this.questionBg.clear();
        this.questionBg.fillStyle(0xF44336, 0.9);
        this.questionBg.fillRoundedRect(-200, -80, 400, 160, 16);

        // Shake effect
        const originalX = this.container.x;
        this.scene.tweens.add({
            targets: this.container,
            x: originalX + 10,
            duration: 50,
            yoyo: true,
            repeat: 3,
            onComplete: () => {
                this.container.x = originalX;
            }
        });

        this.scene.time.delayedCall(300, () => {
            this.questionBg.clear();
            this.questionBg.fillStyle(0xFFFFFF, 0.95);
            this.questionBg.fillRoundedRect(-200, -80, 400, 160, 16);
            this.questionBg.lineStyle(3, CONSTANTS.COLORS.CHEERFUL_ACCENT);
            this.questionBg.strokeRoundedRect(-200, -80, 400, 160, 16);
        });
    }

    setDifficulty(level) {
        this.difficulty = level;
    }

    setDepth(depth) {
        this.container.setDepth(depth);
    }

    getContainer() {
        return this.container;
    }

    destroy() {
        this.container.destroy();
    }
}
